import jsPDF from 'jspdf';

/**
 * Progress callback type for PDF generation
 */
export type PDFProgressCallback = (stage: string, progress?: number) => void;

/**
 * Generates a PDF from a canvas and saves it
 * Includes better validation and error handling
 */
export const generateAndSavePDF = (
  canvas: HTMLCanvasElement,
  stageCount: number,
  mapName?: string,
  progressCallback?: PDFProgressCallback
): void => {
  try {
    // Validate canvas
    if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
      throw new Error("Invalid canvas: Canvas is empty or has invalid dimensions");
    }
    
    progressCallback?.("converting", 10);
    
    // Convert to image data with error handling
    let imgData: string;
    try {
      imgData = canvas.toDataURL('image/png', 1.0); // Use maximum quality
      if (!imgData || imgData === 'data:,') {
        throw new Error("Failed to generate image data from canvas");
      }
    } catch (err) {
      console.error("Canvas conversion error:", err);
      throw new Error("Failed to convert journey map to image");
    }
    
    progressCallback?.("formatting", 30);
    
    // Determine best orientation based on aspect ratio and stage count
    // For wide journey maps or maps with many stages, use landscape
    const aspectRatio = canvas.width / canvas.height;
    const orientation = (aspectRatio > 1 || stageCount > 3) ? 'landscape' : 'portrait';
    
    // Create PDF with appropriate orientation and unit (mm)
    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    // Add a safe default map name if none provided
    const safeMapName = mapName && mapName.trim() ? 
      mapName.trim().replace(/[^a-zA-Z0-9\s-_]/g, '_') : 
      'journey_map';
    
    progressCallback?.("sizing", 50);
    
    // Position and size the image on the PDF
    const pdfDimensions = calculatePDFDimensions(canvas, pdf, orientation);
    
    progressCallback?.("adding", 70);
    
    // Add the image to the PDF
    pdf.addImage(
      imgData, 
      'PNG', 
      pdfDimensions.xPos, 
      pdfDimensions.yPos, 
      pdfDimensions.scaledWidth, 
      pdfDimensions.scaledHeight
    );
    
    // Add a subtle border around the image to define its boundaries
    pdf.setDrawColor(200, 200, 200); // Light gray
    pdf.setLineWidth(0.2);
    pdf.rect(
      pdfDimensions.xPos, 
      pdfDimensions.yPos, 
      pdfDimensions.scaledWidth, 
      pdfDimensions.scaledHeight
    );
    
    progressCallback?.("saving", 90);
    
    // Generate timestamp for filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${safeMapName}_${timestamp}.pdf`;
    
    // Save the PDF
    pdf.save(filename);
    
    progressCallback?.("complete", 100);
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  }
};

/**
 * Calculates the dimensions for the PDF
 */
interface PDFDimensions {
  scaledWidth: number;
  scaledHeight: number;
  xPos: number;
  yPos: number;
}

const calculatePDFDimensions = (
  canvas: HTMLCanvasElement,
  pdf: jsPDF,
  orientation: 'portrait' | 'landscape'
): PDFDimensions => {
  // Get canvas dimensions (in pixels)
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  
  // Get PDF dimensions (in mm)
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // Set a safe margin (in mm)
  const margin = 15;
  
  // Calculate the available space within margins (in mm)
  const availableWidth = pdfWidth - (margin * 2);
  const availableHeight = pdfHeight - (margin * 2);
  
  // Convert pixel dimensions to mm using PDF's internal scale
  const pixelsPerMm = pdf.internal.scaleFactor;
  const imgWidthMm = imgWidth / pixelsPerMm;
  const imgHeightMm = imgHeight / pixelsPerMm;
  
  // Calculate scale factor to fit image within available space
  const scaleWidth = availableWidth / imgWidthMm;
  const scaleHeight = availableHeight / imgHeightMm;
  const scale = Math.min(scaleWidth, scaleHeight);
  
  // Calculate dimensions in mm
  const scaledWidthMm = imgWidthMm * scale;
  const scaledHeightMm = imgHeightMm * scale;
  
  // Center the image on the page
  const xPos = margin + (availableWidth - scaledWidthMm) / 2;
  const yPos = margin + (availableHeight - scaledHeightMm) / 2;
  
  return {
    scaledWidth: scaledWidthMm,
    scaledHeight: scaledHeightMm,
    xPos: xPos,
    yPos: yPos
  };
};
